import React from 'react';
import { Element as ElementType } from '../types';
import { Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { FaStar } from 'react-icons/fa';
import './animations.css';
import { useInteractionMode } from '../contexts/InteractionModeContext';

interface MachineSlotProps {
  element: ElementType;
  slotType: 'input' | 'output';
  onDragEnd?: () => void;
  onClear?: () => void;
  isNew?: boolean;
  disableDrag?: boolean;
}

const MachineSlot: React.FC<MachineSlotProps> = ({
  element,
  slotType,
  onDragEnd,
  onClear,
  isNew = false,
  disableDrag = false,
}) => {
  // Use the element's color directly
  const bgColor =
    element.color ||
    (() => {
      // Fallback colors if element.color is not available
      switch (element.type) {
        case 'water':
          return '#3b82f6';
        case 'fire':
          return '#ef4444';
        case 'dirt':
          return '#8B6F4E';
        case 'air':
          return '#e2e8f0';
        default:
          return '#8b5cf6';
      }
    })();

  // Get text color based on background color
  const getTextColor = (bgColor: string) => {
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const textColor = getTextColor(bgColor);

  // Get interaction mode from context
  const { mode, selectedElement, setSelectedElement } = useInteractionMode();

  // Set up drag functionality for both input and output elements - simplified
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: 'element',
      item: element,
      end: (item, monitor) => {
        if (onDragEnd) {
          onDragEnd();
        }
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      // Disable drag in tap-to-location mode or if disableDrag is true
      canDrag: mode === 'drag-and-drop' && !disableDrag,
    }),
    [element, onDragEnd, mode, disableDrag]
  );

  // Use an empty image as the drag preview (we'll create our own custom preview)
  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Handle tap/click for tap-to-location mode
  const handleTap = () => {
    if (mode === 'tap-to-location') {
      // If we have a selected element, we don't want to select this one
      if (selectedElement) {
        return;
      }

      // Select this element
      setSelectedElement(element);
    }
  };

  // Same styles for both input and output slots
  const slotStyles = {
    backgroundColor: bgColor,
    color: textColor,
    width: '100%',
    height: '100%',
    minHeight: '100%',
    opacity: isDragging ? 0.5 : 1,
    cursor: mode === 'drag-and-drop' ? 'grab' : 'pointer',
  };

  return (
    <div
      ref={drag}
      onClick={handleTap}
      className={`w-full h-full rounded-lg shadow-md flex items-center justify-center relative
        ${slotType === 'output' ? 'animate-pop' : ''}
        ${isDragging ? 'opacity-50' : ''}
        ${
          selectedElement?.id === element.id
            ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
            : ''
        }
      `}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        cursor: mode === 'drag-and-drop' ? 'grab' : 'pointer',
      }}
    >
      {onClear && (
        <Button
          variant="destructive"
          size="icon"
          className="absolute top-1 right-1 h-6 w-6 opacity-80 hover:opacity-100"
          onClick={onClear}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
      <span className="text-center font-medium select-none px-2 py-1 text-xs sm:text-sm truncate max-w-full overflow-hidden">
        {element.name}
      </span>
    </div>
  );
};

export default MachineSlot;
